import React, { useState } from 'react';
import axios from 'axios';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { trackWaitlistLead } from '../lib/analytics';
import '../styles/resfi.css';
import '../styles/resfi-landing.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!validateEmail(em)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/waitlist/email`, { email: em.toLowerCase(), source: 'home' });
      setSubmitted(true);
      if (!res.data?.already_existed) {
        trackWaitlistLead('Waitlist Home Signup', 'Waitlist Signup');
      }
    } catch (err) {
      setSubmitting(false);
      setError('Something went wrong — please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="success show" data-testid="waitlist-success">
        You're on the list. We'll be in touch soon.
      </div>
    );
  }

  return (
    <>
      <form className="form" onSubmit={handleSubmit} noValidate data-testid="waitlist-form">
        <input
          type="email"
          name="email"
          placeholder="you@email.com"
          aria-label="Email address"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
          data-testid="waitlist-email-input"
        />
        <button type="submit" disabled={submitting} data-testid="waitlist-submit-button">
          {submitting ? 'Joining…' : (
            <>
              Join the waitlist
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </>
          )}
        </button>
      </form>
      {error && <p className="form-error" data-testid="waitlist-error">{error}</p>}
      <p className="form-note">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
        It's free, takes seconds, and there's no impact to your credit.
      </p>
    </>
  );
};

const LandingPage = () => {
  return (
    <>
      <Nav />
      <div className="resfi-home" data-testid="landing-page">

      {/* HERO */}
      <section className="hero" id="top">
        <div className="wrap">
          <span className="eyebrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
            The autonomous CFO for your debt
          </span>
          <h1>Pay off your cards faster,<br /><span className="grad">without changing how you live</span></h1>
          <p className="sub">resfi quietly puts your excess cash to work against your credit card debt, automatically. Saving you thousands in interest and getting you debt-free years sooner. No lifestyle changes required.</p>

          <div id="waitlist-form"><WaitlistForm /></div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="strip">
        <div className="wrap strip-inner">
          <div className="strip-label">Built on infrastructure trusted across modern finance</div>
          <div className="badges">
            <span className="badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>256-bit AES</span>
            <span className="badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10l9-7 9 7" /><path d="M5 9v11h14V9" /></svg>FDIC partner bank</span>
            <span className="badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>Secure by design</span>
          </div>
        </div>
      </div>

      {/* AUTOMATE FEATURE */}
      <section className="feature" data-testid="automate-section">
        <div className="wrap feature-grid">
          <div>
            <span className="tag">Automate</span>
            <h2>AI-native banking that <span className="serif">acts</span>, not just answers.</h2>
            <p>resfi is agentic by design. It watches your cash, moves money against your debt, and adjusts the plan on its own, every day, without you lifting a finger. When you do want to weigh in, just ask in plain language and it handles the decision for you. No spreadsheets, no juggling multiple apps. We just do it for you.</p>
          </div>
          <div className="panel">
            <div className="chat">
              <div className="bub ai">
                <span className="who">resfi <span className="auto-chip">Auto</span></span>
                You had $220 sitting idle in checking, so I moved it to your card. That's $31 of interest you won't pay.
              </div>
              <div className="bub me">
                <span className="who">You</span>
                Nice. How fast am I debt-free now?
              </div>
              <div className="bub ai">
                <span className="who">resfi</span>
                March 2027, three weeks sooner than yesterday. Want me to keep a $40 buffer or push it all next month too?
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REWARD FEATURE */}
      <section className="feature alt">
        <div className="wrap feature-grid">
          <div className="panel">
            <div className="row">
              <div className="row-l">
                <div className="row-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div>
                <div><div className="row-t">6-month on-time streak</div><div className="row-s">Never missed a payment</div></div>
              </div>
              <span className="rew">APR −0.50%</span>
            </div>
            <div className="row">
              <div className="row-l">
                <div className="row-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg></div>
                <div><div className="row-t">Paid $75 over your minimum</div><div className="row-s">Applied straight to principal</div></div>
              </div>
              <span className="rew">−18 days</span>
            </div>
            <div className="row win">
              <div className="win-t">Rewards auto-applied → debt-free 5 mo sooner</div>
              <span className="pos">+$820</span>
            </div>
          </div>
          <div>
            <span className="tag">Reward</span>
            <h2>Good behavior, automatically <span className="serif">rewarded</span>.</h2>
            <p>Every on-time payment and every extra dollar earns you a lower rate and an earlier payoff date. No points, no tiers, nothing to claim. resfi applies each reward the moment you earn it, so your progress compounds on its own and your debt shrinks faster than you expect.</p>
          </div>
        </div>
      </section>

      {/* ANALYZE FEATURE */}
      <section className="feature">
        <div className="wrap feature-grid">
          <div>
            <span className="tag">Analyze</span>
            <h2>Spending &amp; subscriptions, surfaced.</h2>
            <p>resfi finds the recurring charges quietly draining your account and shows what cancelling them frees up. Run what-if scenarios and watch your debt-free date jump forward in real time.</p>
          </div>
          <div className="panel">
            <div className="row">
              <div className="row-l">
                <div className="row-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /></svg></div>
                <div><div className="row-t">Streaming bundle</div><div className="row-s">3 services overlap</div></div>
              </div>
              <span className="neg">−$47/mo</span>
            </div>
            <div className="row">
              <div className="row-l">
                <div className="row-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><line x1="8" y1="12" x2="16" y2="12" /></svg></div>
                <div><div className="row-t">Unused gym + apps</div><div className="row-s">No activity 90 days</div></div>
              </div>
              <span className="neg">−$68/mo</span>
            </div>
            <div className="row win">
              <div className="win-t">Redirect $115/mo → debt-free 7 mo sooner</div>
              <span className="pos">+$1,290</span>
            </div>
          </div>
        </div>
      </section>

      {/* SAFE / TRUST SECTION */}
      <div className="safe">
        <div className="wrap">
          <div className="safe-eyebrow">Built to be trusted with your money</div>
          <h2>Responsible finance means <span className="serif">safe</span> finance.</h2>
          <p className="lede">We're not a debt-settlement company and we'll never tell you to stop paying your bills. resfi is built to protect your money, your data, and your credit.</p>
          <div className="cards">
            <div className="c">
              <div className="c-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></div>
              <h3>Bank-grade encryption</h3>
              <p>256-bit AES in transit and at rest, with read-only account connections. We never store your card logins and never move money without your authorization.</p>
            </div>
            <div className="c">
              <div className="c-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg></div>
              <h3>Designed to lift your credit</h3>
              <p>Checking your plan is a soft inquiry — zero score impact. By clearing balances and ending missed payments, resfi is built to help your score, then rewards you for it.</p>
            </div>
            <div className="c">
              <div className="c-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10l9-7 9 7" /><path d="M5 9v11h14V9" /></svg></div>
              <h3>Regulated partners</h3>
              <p>Deposits and lending are handled by <em>FDIC</em>-member partner banks and licensed lenders. resfi builds the plan; trusted, regulated institutions hold and move the money.</p>
            </div>
          </div>
        </div>
      </div>

      {/* FINAL CTA */}
      <section className="final" id="join">
        <div className="wrap">
          <span className="final-eyebrow">Join the waitlist</span>
          <h2>Does your credit card company do <span className="serif">this</span> for you?</h2>
          <p className="lede">Lower rates for paying on time. Months shaved off your payoff date, automatically. Zero penalties for doing the right thing. If your card won't reward you for good behavior, resfi will. Join the waitlist to start saving.</p>
          <a href="#waitlist-form" className="final-btn" data-testid="final-join-button">Join the waitlist
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </a>
        </div>
      </section>
      </div>
      <Footer />
    </>
  );
};

export default LandingPage;
